import { useEffect, useState, type ChangeEvent } from 'react'
import { api, type CapsuleOptions, type Product } from '../lib/api.ts'
import {
  chosenTraits,
  isShell,
  shellChoiceChanged,
  shellTraitsInForce,
  traitLabels,
  traitOnAxis,
} from '../lib/capsuleShells.ts'
import { Button } from './Button.tsx'
import { Select } from './Select.tsx'
import { Tag } from './Tag.tsx'
import { useConfirm } from './Confirm.tsx'
import { useToast } from './Toast.tsx'

/**
 * What this product is as a capsule shell, at this pharmacy (#608, #636,
 * ADR 0042).
 *
 * A verification reads a shell from its name -- `CAP GELAT INCOL 1` is
 * gelatine and colourless -- by ArtPharma's naming habits. Here somebody says
 * what it really is when the name misleads, or marks as a shell a product
 * whose name starts like none (`INVOLUCRO GELATINA 0`), so it is recognised on
 * the orçamento at all.
 */
export function ShellCorrection({
  accountId,
  product,
  options,
  onChanged,
}: {
  accountId: string | undefined
  product: Product
  options: CapsuleOptions
  onChanged: () => void
}) {
  const { shell } = product
  const [byAxis, setByAxis] = useState<Record<string, string>>(() => choiceOf(shell, options))
  const [busy, setBusy] = useState(false)
  const toast = useToast()
  const confirm = useConfirm()
  const chosen = chosenTraits(byAxis)

  // A reload brings what is stored; the choices follow it.
  useEffect(() => {
    setByAxis(choiceOf(shell, options))
  }, [shell, options])

  async function run(action: () => Promise<unknown>, done: string) {
    if (accountId === undefined) return
    setBusy(true)
    try {
      await action()
      toast.show(done, { tone: 'success' })
      onChanged()
    } catch (cause) {
      toast.show(cause instanceof Error ? cause.message : 'falha ao salvar', { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  async function takeBack() {
    if (accountId === undefined) return
    const sure = await confirm(
      shell.byName
        ? {
            title: `Voltar a ler "${product.name}" pelo nome?`,
            message: 'Nas próximas conferências, este invólucro passa a ser lido pelo nome dele.',
            confirmLabel: 'Voltar ao nome',
          }
        : {
            title: `Deixar de tratar "${product.name}" como invólucro?`,
            message:
              'Nas próximas conferências, este produto deixa de ser lido como o invólucro da cápsula.',
            confirmLabel: 'Deixar de ser invólucro',
          },
    )
    if (!sure) return
    await run(
      () => api.clearShellTraits(accountId, product.erpCode),
      shell.byName ? 'Invólucro lido pelo nome' : 'Produto deixa de ser invólucro',
    )
  }

  const tags = (traits: readonly string[]) =>
    traits.length === 0 ? (
      <span className="text-text-weak">não diz</span>
    ) : (
      <span className="flex flex-wrap gap-1">
        {traitLabels(traits, options.traits).map((label) => (
          <Tag key={label} variant="stroke">
            {label}
          </Tag>
        ))}
      </span>
    )

  const corrected = shell.corrected
  return (
    <div className="flex flex-col items-start gap-3">
      {shell.byName ? (
        <div className="text-tP flex flex-wrap items-center gap-2">
          <span className="text-text-weak">Lido do nome:</span>
          {tags(shell.read)}
        </div>
      ) : (
        corrected === null && (
          <p className="text-tP text-text-weak">
            O nome não começa como o de um invólucro, então a conferência não o lê como um.
          </p>
        )
      )}
      {corrected !== null && (
        <div className="flex flex-col gap-1">
          <div className="text-tP flex flex-wrap items-center gap-2">
            <span className="text-text-weak">Nesta farmácia:</span>
            {tags(corrected.traits)}
          </div>
          <p className="text-tSmall text-text-weak">
            {`Definido por ${corrected.by} em ${new Date(corrected.at).toLocaleString('pt-BR', {
              dateStyle: 'short',
              timeStyle: 'short',
            })}`}
          </p>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {options.axes.map((axis) => (
          <Select
            key={axis.id}
            size="small"
            label={axis.label}
            value={byAxis[axis.id] ?? ''}
            disabled={busy}
            onChange={(event: ChangeEvent<HTMLSelectElement>) =>
              setByAxis((current) => ({ ...current, [axis.id]: event.target.value }))
            }
          >
            <option value="">Não diz</option>
            {options.traits
              .filter((trait) => trait.axis === axis.id)
              .map((trait) => (
                <option key={trait.id} value={trait.id}>
                  {trait.label}
                </option>
              ))}
          </Select>
        ))}
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          size="small"
          variant="secondary"
          tone="brand"
          disabled={busy || accountId === undefined || !shellChoiceChanged(shell, chosen)}
          onClick={() =>
            void run(
              () => api.setShellTraits(accountId ?? '', product.erpCode, chosen),
              isShell(shell) ? 'Invólucro corrigido' : 'Produto marcado como invólucro',
            )
          }
        >
          {busy ? 'Salvando…' : isShell(shell) ? 'Salvar invólucro' : 'Marcar como invólucro'}
        </Button>
        {corrected !== null && (
          <Button
            size="small"
            variant="secondary"
            tone="neutral"
            disabled={busy || accountId === undefined}
            onClick={() => void takeBack()}
          >
            {shell.byName ? 'Voltar ao nome' : 'Deixar de ser invólucro'}
          </Button>
        )}
      </div>
    </div>
  )
}

/** One choice per axis, starting from what the shell counts as now. */
function choiceOf(shell: Product['shell'], options: CapsuleOptions): Record<string, string> {
  const inForce = shellTraitsInForce(shell)
  return Object.fromEntries(
    options.axes.map((axis) => [axis.id, traitOnAxis(inForce, axis.id, options.traits)]),
  )
}
